/** Settings: this account, and where output files are written. */

import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api, type StoragePreview, type StorageConfig } from "../api";
import { keys } from "../queries";
import { useAuth } from "../auth";
import { Layout } from "../components/Layout";
import {
  Badge,
  Banner,
  Button,
  Card,
  Code,
  Field,
  Modal,
  Muted,
  Select,
  Spinner,
  TableScroll,
  TextInput,
} from "../components/ui";

export function Settings() {
  return (
    <Layout
      breadcrumbs={[{ label: "Settings" }]}
      title="Settings"
      description="Who you are to the portal, and where the files it produces are written."
    >
      <div className="flex flex-col gap-lg">
        <AccountSection />
        <PasswordSection />
        <StorageSection />
      </div>
    </Layout>
  );
}

function AccountSection() {
  const { user, refresh } = useAuth();
  const [displayName, setDisplayName] = useState(user?.display_name ?? "");
  const [email, setEmail] = useState(user?.email ?? "");

  useEffect(() => {
    setDisplayName(user?.display_name ?? "");
    setEmail(user?.email ?? "");
  }, [user]);

  const saveProfile = useMutation({
    mutationFn: () => api.updateAccount({ display_name: displayName, email }),
    // The name saved here is the name every Run is attributed to, so the
    // signed-in user has to be re-read before the shell repaints it.
    onSuccess: refresh,
  });

  const error = asApiError(saveProfile.error, "Could not save.");
  const notice = saveProfile.isSuccess ? "Your details are saved." : null;

  return (
    <Card title="Your account" description="The portal attributes activity to this name.">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          saveProfile.mutate();
        }}
      >
        <div className="flex flex-col gap-md">
          {error && <Banner tone="danger" title={error.message} />}
          {notice && <Banner tone="success" title={notice} />}

          <div className="grid grid-cols-1 gap-md fold:grid-cols-2">
            <Field label="Display name">
              <TextInput value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
            </Field>
            <Field label="Email">
              <TextInput type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </Field>
          </div>

          <div>
            <Button type="submit" variant="primary" disabled={saveProfile.isPending}>
              {saveProfile.isPending ? "Saving…" : "Save details"}
            </Button>
          </div>
        </div>
      </form>
    </Card>
  );
}

function PasswordSection() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const savePassword = useMutation({
    mutationFn: () =>
      api.changePassword({ current_password: currentPassword, new_password: newPassword }),
    onSuccess: () => {
      setCurrentPassword("");
      setNewPassword("");
    },
  });

  const error = asApiError(savePassword.error, "Could not change it.");
  const notice = savePassword.data
    ? savePassword.data.sessions_invalidated > 0
      ? `Password changed. ${savePassword.data.sessions_invalidated} other session${
          savePassword.data.sessions_invalidated === 1 ? " was" : "s were"
        } signed out.`
      : "Password changed."
    : null;

  return (
    <Card title="Change your password" description="Changing your password signs out every other browser.">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          savePassword.mutate();
        }}
      >
        <div className="flex flex-col gap-md">
          {error && <Banner tone="danger" title={error.message} />}
          {notice && <Banner tone="success" title={notice} />}

          <div className="grid grid-cols-1 gap-md fold:grid-cols-2">
            <Field
              label="Current password"
              hint="Required, so a hijacked session cannot lock you out of your own account."
            >
              <TextInput
                type="password"
                value={currentPassword}
                autoComplete="current-password"
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
            </Field>
            <Field label="New password">
              <TextInput
                type="password"
                value={newPassword}
                autoComplete="new-password"
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </Field>
          </div>

          <div>
            <Button
              type="submit"
              variant="primary"
              disabled={!currentPassword || !newPassword || savePassword.isPending}
            >
              {savePassword.isPending ? "Changing…" : "Change password"}
            </Button>
          </div>
        </div>
      </form>
    </Card>
  );
}

function StorageSection() {
  const queryClient = useQueryClient();

  const { data: stored, error: loadError } = useQuery({
    queryKey: keys.storage,
    queryFn: api.readStorage,
  });

  /**
   * The edited copy, held separately from the fetched one.
   *
   * A background refetch — this query refetches when the tab regains focus —
   * must not overwrite half-typed settings, so the draft wins whenever it
   * exists and is dropped back to `null` once a save has landed. That is also
   * what makes "reset" free: forgetting the draft is the whole of it.
   */
  const [draft, setDraft] = useState<StorageConfig | null>(null);
  const config = draft ?? stored ?? null;

  const validate = useMutation({ mutationFn: api.validateStorage });

  /**
   * Whether the paths are being looked at.
   *
   * The list is nine paths per source and the Workspace has one per source, so
   * it is longer than the form it belongs to and nobody reads it while typing a
   * bucket name. It opens on request, which is also what keeps the query below
   * from firing on every keystroke.
   */
  const [pathsOpen, setPathsOpen] = useState(false);

  /**
   * The paths this destination will produce, re-asked as the fields change.
   *
   * Derived on the server by the same module that composes a Run's keys
   * (ADR-0005), never assembled here. A preview built in the frontend would be
   * a second copy of the naming standard, and the first time the two disagreed
   * the screen would be confidently wrong about where production data lands.
   *
   * Keyed on the draft rather than on what is stored, so opening this after
   * editing the bucket shows where the files would land if the edit were saved.
   * `region` is absent from the key deliberately: it selects an endpoint, not a
   * key, so a change to it cannot move a file.
   */
  const preview = useQuery({
    queryKey: [
      "storage-preview",
      config?.provider,
      config?.bucket,
      config?.prefix,
      config?.local_root,
    ],
    queryFn: () =>
      api.previewStorage({
        provider: config!.provider,
        bucket: config!.bucket,
        prefix: config!.prefix,
        region: config!.region,
        local_root: config!.local_root,
      }),
    enabled: pathsOpen && Boolean(config),
  });

  const save = useMutation({
    mutationFn: (next: StorageConfig) => api.updateStorage(next),
    onSuccess: (saved) => {
      queryClient.setQueryData(keys.storage, saved);
      setDraft(null);
      // A check against the previous destination says nothing about this one.
      validate.reset();
    },
  });

  const busy = save.isPending || validate.isPending;
  const validation = validate.data ?? null;
  const error =
    messageFor(loadError, "Could not load the storage settings.") ??
    messageFor(save.error, "Could not save.") ??
    messageFor(validate.error, "Could not check the destination.");

  if (!config) {
    return (
      <Card title="Where output files are written">
        {error ? <Banner tone="danger" title={error} /> : <Spinner label="Loading storage settings" />}
      </Card>
    );
  }

  return (
    <Card
      title="Where output files are written"
      description="Check the destination before you rely on it. Otherwise a bad one surfaces at the end of a long Run, after all the work is done."
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate(config);
        }}
      >
        <div className="flex flex-col gap-md">
          {error && <Banner tone="danger" title={error} />}
          {validation && (
            <Banner
              tone={validation.ok ? "success" : "danger"}
              title={
                validation.ok
                  ? "The destination is reachable and writable."
                  : "The destination is not usable."
              }
            >
              {validation.message}
            </Banner>
          )}

          <Field label="Provider">
            <Select
              value={config.provider}
              onChange={(e) => setDraft({ ...config, provider: e.target.value as "local" | "s3" })}
            >
              <option value="s3">Amazon S3</option>
              <option value="local">A directory on this server</option>
            </Select>
          </Field>

          {config.provider === "s3" ? (
            <div className="grid grid-cols-1 gap-md fold:grid-cols-3">
              <Field label="Bucket">
                <TextInput
                  value={config.bucket ?? ""}
                  placeholder="dsa-labs"
                  onChange={(e) => setDraft({ ...config, bucket: e.target.value })}
                />
              </Field>
              <Field label="Path inside the bucket" hint="Optional. Leave empty to write at the root.">
                <TextInput
                  value={config.prefix ?? ""}
                  placeholder="pff-data-aggregation"
                  onChange={(e) => setDraft({ ...config, prefix: e.target.value })}
                />
              </Field>
              <Field label="Region">
                <TextInput
                  value={config.region ?? ""}
                  onChange={(e) => setDraft({ ...config, region: e.target.value })}
                />
              </Field>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-md fold:grid-cols-[2fr_1fr]">
              <Field label="Directory">
                <TextInput
                  value={config.local_root ?? ""}
                  onChange={(e) => setDraft({ ...config, local_root: e.target.value })}
                />
              </Field>
              <Field label="Path inside it" hint="Optional.">
                <TextInput
                  value={config.prefix ?? ""}
                  onChange={(e) => setDraft({ ...config, prefix: e.target.value })}
                />
              </Field>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-xs">
            <Button type="submit" variant="primary" disabled={busy}>
              {save.isPending ? "Saving…" : "Save destination"}
            </Button>
            <Button onClick={() => validate.mutate()} disabled={busy}>
              {validate.isPending ? "Checking…" : "Check it now"}
            </Button>
            {/* The naming convention itself, which the three fields above are
                not. Beside the destination controls because it is an answer
                about them, and behind a press because it is longer than they
                are. */}
            <Button onClick={() => setPathsOpen(true)} aria-expanded={pathsOpen}>
              Folder paths and file names
            </Button>
          </div>

          {config.updated_by && <Muted>Last changed by {config.updated_by}.</Muted>}
        </div>
      </form>

      <Modal
        open={pathsOpen}
        onClose={() => setPathsOpen(false)}
        title="Folder paths and file names"
        description="What this destination will write, composed by the same code that writes it."
        footer={
          <Button variant="primary" onClick={() => setPathsOpen(false)}>
            Done
          </Button>
        }
      >
        <StoragePathPreview preview={preview.data ?? null} loading={preview.isPending} />
      </Modal>
    </Card>
  );
}

/**
 * What the files will actually be called, and where they will land.
 *
 * This is the answer to the client's question about path and naming
 * conventions. They were shown a provider, a bucket and a prefix — the two
 * fields that are *not* the convention — and none of the convention itself, so
 * a property the portal already enforced looked absent.
 */
function StoragePathPreview({
  preview,
  loading,
}: {
  preview: StoragePreview | null;
  loading: boolean;
}) {
  if (loading && !preview) return <Muted>Working out the paths…</Muted>;
  if (!preview) return null;

  return (
    <div className="flex flex-col gap-md">
      <Muted>
        Every source follows the same convention under one root, and each resolves to its own
        path. Nothing here is typed — it is composed by the same code that writes the files.
      </Muted>

      {preview.illustrative && (
        <Banner tone="info" title="No sources yet — these are examples">
          Once you add a source, its real paths appear here.
        </Banner>
      )}

      <Muted>
        Root: <Code>{preview.root}</Code>
      </Muted>

      {preview.sources.map((source) => (
        <div key={source.source_profile_id ?? source.name} className="flex flex-col gap-xxs">
          <div className="flex flex-wrap items-center gap-xs">
            <strong className="text-body-sm-medium">{source.name}</strong>
            <Badge>{source.league}</Badge>
            <Badge>{source.season}</Badge>
          </div>
          {source.error ? (
            <Banner tone="danger" title="This source cannot produce a valid path">
              {source.error}
            </Banner>
          ) : (
            <TableScroll>
              <table className="table">
                <tbody>
                  {source.paths.map((path) => (
                    <tr key={path.key}>
                      <td className="whitespace-nowrap">
                        {path.label}
                        {/* Said rather than left to be inferred: two sources in
                            one sport share these, which would otherwise read as
                            the preview contradicting itself about each source
                            having its own destination. */}
                        {path.per_sport && <Badge>shared</Badge>}
                      </td>
                      <td>
                        <Code>{path.key}</Code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          )}
        </div>
      ))}
    </div>
  );
}

/** The human message an API rejection carries, or a fallback for anything else. */
function messageFor(error: unknown, fallback: string): string | null {
  if (!error) return null;
  return error instanceof ApiError ? error.message : fallback;
}

/** The same, kept as an `ApiError` for the sections that render `error.field`. */
function asApiError(error: unknown, fallback: string): ApiError | null {
  if (!error) return null;
  return error instanceof ApiError ? error : new ApiError(0, "unknown", fallback);
}
